import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, ChevronRight, ArrowLeft, Settings } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const StripesApplication = () => {
  const { user, isAuthenticated } = useAuth();
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const isAdmin = isAuthenticated && user?.is_admin;

  useEffect(() => {
    const fetchTutorials = async () => {
      try {
        const response = await axios.get(`${API}/api/tutorials`);
        setTutorials(response.data.tutorials || []);
        if (response.data.tutorials && response.data.tutorials.length > 0) {
          setSelectedTutorial(response.data.tutorials[0]);
        }
      } catch (error) {
        console.error('Failed to fetch tutorials:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTutorials();
  }, []);

  return (
    <div className="pt-24 pb-16" data-testid="stripes-application-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="label-style mb-2">How To Guide</p>
          <h1 
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
          >
            Installing your Graphics
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Watch our video tutorials and follow step-by-step instructions to apply your vinyl decals perfectly.
          </p>
          
          {/* Admin Edit Button - Only visible to admins */}
          {isAdmin && (
            <Link 
              to="/admin/tutorials"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-black text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
              data-testid="admin-edit-tutorials-btn"
            >
              <Settings className="h-4 w-4" />
              Edit Tutorials
            </Link>
          )}
        </div>

        {/* Video Tutorials Section */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
          </div>
        ) : tutorials.length === 0 ? (
          <div className="text-center py-12 bg-secondary/30 rounded-lg">
            <Play className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-600 mb-2">Video Tutorials Coming Soon!</h2>
            <p className="text-gray-500 max-w-md mx-auto">
              We're working on creating helpful video tutorials. Check back soon!
            </p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Video Player Section */}
            <div className="lg:col-span-2">
              {selectedTutorial && (
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-border">
                  <div className="aspect-video bg-black">
                    <iframe
                      src={selectedTutorial.embed_url}
                      title={selectedTutorial.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      data-testid="tutorial-video-player"
                    />
                  </div>
                  <div className="p-6">
                    <h2 
                      className="text-2xl font-bold mb-2" 
                      data-testid="tutorial-title"
                      style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
                    >
                      {selectedTutorial.title}
                    </h2>
                    {selectedTutorial.description && (
                      <p className="text-muted-foreground" data-testid="tutorial-description">
                        {selectedTutorial.description}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Tutorial List Sidebar */}
            <div className="lg:col-span-1">
              <h3 
                className="text-lg font-semibold mb-4 flex items-center gap-2"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                <Play className="h-5 w-5" />
                All Tutorials
              </h3>
              <div className="space-y-3">
                {tutorials.map((tutorial, index) => (
                  <button
                    key={tutorial._id}
                    onClick={() => setSelectedTutorial(tutorial)}
                    className={`w-full text-left p-4 rounded-xl transition-all duration-200 flex items-center gap-4 group ${
                      selectedTutorial?._id === tutorial._id
                        ? 'bg-black text-white shadow-lg'
                        : 'bg-white hover:bg-gray-100 shadow-sm hover:shadow-md border border-border'
                    }`}
                    data-testid={`tutorial-item-${index}`}
                  >
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                      selectedTutorial?._id === tutorial._id ? 'bg-white/20' : 'bg-black/10'
                    }`}>
                      <Play className={`h-5 w-5 ${
                        selectedTutorial?._id === tutorial._id ? 'text-white' : 'text-black'
                      }`} />
                    </div>
                    <div className="flex-grow min-w-0">
                      <h4 className="font-medium truncate">{tutorial.title}</h4>
                      {tutorial.description && (
                        <p className={`text-sm truncate ${
                          selectedTutorial?._id === tutorial._id ? 'text-gray-300' : 'text-gray-500'
                        }`}>
                          {tutorial.description}
                        </p>
                      )}
                    </div>
                    <ChevronRight className={`h-5 w-5 flex-shrink-0 transition-transform group-hover:translate-x-1 ${
                      selectedTutorial?._id === tutorial._id ? 'text-white' : 'text-gray-400'
                    }`} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Additional Tips */}
        <div className="mt-12 bg-secondary/30 p-6 rounded-lg">
          <h3 
            className="font-bold text-lg mb-3"
            style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
          >
            Need Help?
          </h3>
          <p className="text-muted-foreground">
            If you have any questions about applying your stripes or decals, don't hesitate to contact us at{' '}
            <a href="mailto:ministripesuk@gmail.com" className="text-primary hover:underline">
              ministripesuk@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default StripesApplication;
